import type { VMHistoryEntry } from '@nebula/shared/services/instancesApi'
import { formatCurrency } from '@nebula/shared/utils/format'

/**
 * Tradução dos eventos do `audit_logs` para a aba Histórico do cliente (issue #794).
 *
 * O `message` que vem do backend é jargão de operação, em inglês ("VM ... created
 * successfully with VMID 130", "Proxmox error: ..."). Quem lê esta tela é o dono da VM,
 * então quem rotula a linha é a `action` — que é estável — e o detalhe sai do `details`,
 * que carrega os valores (o nome do snapshot, os cores de antes e depois, a porta da
 * regra).
 *
 * Ação desconhecida NÃO é escondida: some da tela seria perder registro real, que é o
 * defeito que esta issue fecha. Ela cai num rótulo genérico com o `message` cru embaixo —
 * feio, porém verdadeiro.
 */

export type NivelDoEvento = 'info' | 'alerta' | 'erro'

export type EventoDescrito = {
  titulo: string
  detalhe: string | null
  nivel: NivelDoEvento
}

const ROTULOS: Record<string, string> = {
  // Ciclo de vida
  vm_creation_completed: 'VM criada',
  vm_creation_task_failed: 'Falha ao criar a VM',
  vm_creation_task_failed_config: 'Falha ao configurar a VM',
  vm_creation_task_retry: 'Nova tentativa de criação',
  vm_deletion_requested: 'Exclusão solicitada',
  vm_deletion_completed: 'VM excluída',
  vm_rebuild_requested: 'Reinstalação solicitada',
  vm_rebuild_completed: 'VM reinstalada',
  vm_rebuild_failed: 'Falha na reinstalação',
  // O PVE recusar é um desfecho de reinstalação como outro qualquer para quem lê: sem
  // rótulo, a linha cairia em "Evento registrado" e mostraria o nome interno da ação.
  vm_rebuild_proxmox_error: 'Falha na reinstalação',

  // Energia — o `vm_action_completed_*` é o nome antigo, da task que não é mais chamada;
  // fica no mapa pelos registros já gravados no banco.
  vm_power_start: 'VM iniciada',
  vm_power_stop: 'VM desligada',
  vm_power_restart: 'VM reiniciada',
  vm_action_completed_start: 'VM iniciada',
  vm_action_completed_stop: 'VM desligada',
  vm_action_completed_restart: 'VM reiniciada',

  // Recursos
  vm_config_updated: 'Configuração alterada',

  // Propriedade (#999). O evento de saída fica com a org antiga e o de entrada com a
  // nova: cada portal lê só o seu, e nenhum dos dois vê o outro lado da transferência.
  vm_adopted: 'VM registrada pelo provedor',
  vm_ownership_transferred_in: 'VM recebida por transferência',
  vm_ownership_transferred_out: 'VM transferida para outra organização',
  vm_ownership_reseller_changed: 'VM passou a ser atendida por outra revenda',

  // Rede
  // A troca de VPC tem três desfechos possíveis no PVE, e a linha diz qual foi: uma
  // pendente só passa a valer no próximo boot, e afirmá-la como feita mandaria o cliente
  // procurar a VM numa rede em que ela ainda não responde.
  vm_network_changed: 'VM movida de VPC',
  vm_network_changed_pendente: 'Troca de VPC pendente (vale no próximo boot)',
  vm_network_changed_nao_confirmado: 'Troca de VPC não confirmada',
  // O backfill da #791 registra, a partir da config no Proxmox, a VPC em que a VM já
  // estava — nada mudou de rede; a linha só diz que a plataforma passou a saber.
  vm_network_pool_backfilled: 'VPC da VM registrada',
  vm_network_mode_public_bridge: 'VM exposta na internet',
  vm_network_mode_public_bridge_incompleta: 'Exposição na internet incompleta',
  vm_network_mode_vpc: 'VM devolvida para a VPC',
  vm_network_mode_vpc_incompleta: 'Retorno para a VPC incompleto',
  vm_network_mode_vpc_pendente: 'Retorno para a VPC pendente',
  vm_nic_added: 'Placa de rede adicionada',
  vm_nic_removed: 'Placa de rede removida',
  vm_nic_updated: 'Placa de rede alterada',
  // O cloud-init é reescrito na hora, mas quem aplica é o guest no próximo boot: a linha
  // promete o que vai valer, não o que já vale.
  vm_static_ip_configurado_pendente: 'IP estático definido (vale no próximo boot)',
  vm_static_ip_removido_pendente: 'Volta para DHCP definida (vale no próximo boot)',
  // Nomes antigos, pelos registros já gravados no banco.
  vm_static_ip_configured: 'IP estático configurado',
  vm_static_ip_removed: 'Rede voltou para DHCP',

  // Firewall
  vm_firewall_rule_created: 'Regra de firewall criada',
  vm_firewall_rule_updated: 'Regra de firewall alterada',
  vm_firewall_rule_deleted: 'Regra de firewall removida',
  vm_firewall_enabled: 'Firewall ativado',
  vm_firewall_disabled: 'Firewall desativado',
  vm_firewall_options_updated: 'Opções de firewall alteradas',
  // A ordem decide qual regra casa primeiro, e um grupo traz um conjunto de regras de uma
  // vez: as duas mudam o acesso efetivo à VM sem criar nem apagar regra avulsa.
  vm_firewall_rules_reordered: 'Ordem das regras de firewall alterada',
  vm_security_group_attached: 'Grupo de segurança aplicado',
  vm_security_group_detached: 'Grupo de segurança removido',

  // Credenciais — o pedido e o desfecho são linhas distintas: a troca acontece dentro do
  // guest e pode falhar, e "senha redefinida" numa senha que não mudou é o tipo de
  // afirmação que esta issue existe para tirar da tela.
  vm_credentials_reset_requested: 'Redefinição de senha solicitada',
  reset_credentials_completed: 'Senha redefinida',
  // Numa VM parada o que existe é o `cipassword` gravado; quem aplica é o cloud-init no
  // próximo boot. Anunciar "senha redefinida" mandaria o cliente tentar entrar com uma
  // senha que a máquina ainda não conhece.
  reset_credentials_pendente: 'Senha definida (vale no próximo boot)',
  reset_credentials_retry: 'Nova tentativa de redefinir a senha',
  reset_credentials_error: 'Falha ao redefinir a senha',

  // Snapshots
  vm_snapshot_create_requested: 'Snapshot solicitado',
  snapshot_create_completed: 'Snapshot criado',
  snapshot_create_retry: 'Nova tentativa de criar o snapshot',
  snapshot_create_error: 'Falha ao criar o snapshot',
  vm_snapshot_delete_requested: 'Remoção de snapshot solicitada',
  snapshot_delete_completed: 'Snapshot removido',
  snapshot_delete_retry: 'Nova tentativa de remover o snapshot',
  snapshot_delete_error: 'Falha ao remover o snapshot',
  vm_snapshot_rollback_requested: 'Restauração de snapshot solicitada',
  snapshot_rollback_completed: 'VM restaurada para o snapshot',
  snapshot_rollback_retry: 'Nova tentativa de restaurar o snapshot',
  snapshot_rollback_error: 'Falha ao restaurar o snapshot',

  // Backup
  vm_backup_requested: 'Backup solicitado',
  vm_backup_restore_requested: 'Restauração de backup solicitada',

  // Operação da plataforma
  vm_credit_deduction_failed: 'Falha ao debitar o crédito',
  // NÃO liste aqui evento que a aba não pode receber. `vm_added_to_ha` e
  // `vm_ha_unavailable` são gravados com `resource_type='proxmox_vm'`, e
  // `vm_suspended_running_remediated`, `vm_cancelled_running_remediated` e
  // `vm_login_user_ausente` saem sem `organization_id` — nenhum deles casa o filtro do
  // histórico. Rótulo para evento que nunca chega engana quem mantiver este arquivo:
  // parece cobertura, e some da tela do mesmo jeito. Se um dia passarem a ser gravados
  // com o par certo, o rótulo entra junto (e o `message` de `vm_added_to_ha`, que cita o
  // node, precisa ser redigido antes).
}

/** Rótulo dos campos de `vm_config_updated`, na ordem em que a tela os mostra. */
const CAMPOS_DE_CONFIG: [string, string, (v: any) => string][] = [
  ['instance_name', 'Nome', (v) => String(v ?? '—')],
  ['cpu_cores', 'CPU', (v) => `${formataNumero(v)} vCPU`],
  ['memory_gb', 'Memória', (v) => `${formataNumero(v)} GB`],
  ['storage_gb', 'Disco', (v) => `${formataNumero(v)} GB`],
  // O resize recalcula o preço da VM; o cliente precisa ver essa mudança ao lado do que a
  // causou — é a linha que permite conferir a fatura contra o histórico.
  ['unit_price', 'Preço', (v) => formatCurrency(Number(v) || 0)],
  ['instance_notes', 'Descrição', (v) => (v ? String(v) : '—')],
]

/**
 * `2` em vez de `2.0`, preservando a fração quando existir.
 *
 * Os números chegam de colunas `Numeric` do backend, serializadas como float: `2.0 vCPU`
 * na tela pareceria precisão que a plataforma não tem.
 */
function formataNumero(valor: any): string {
  const n = Number(valor)
  return Number.isFinite(n) ? String(n) : String(valor ?? '—')
}

function detalheDaConfig(details: Record<string, any>): string | null {
  const changes = details?.changes
  if (!changes || typeof changes !== 'object') return null

  const partes = CAMPOS_DE_CONFIG.filter(([campo]) => changes[campo]).map(
    ([campo, rotulo, formata]) =>
      `${rotulo}: ${formata(changes[campo].old)} → ${formata(changes[campo].new)}`,
  )
  // Campo que a tela ainda não conhece não pode sumir: sem isto, um `changes` novo viraria
  // uma linha "Configuração alterada" sem dizer o que mudou.
  const conhecidos = new Set(CAMPOS_DE_CONFIG.map(([campo]) => campo))
  const extras = Object.keys(changes).filter((campo) => !conhecidos.has(campo))
  partes.push(...extras.map((campo) => `${campo}: ${changes[campo].old} → ${changes[campo].new}`))

  return partes.length > 0 ? partes.join(' · ') : null
}

function detalheDoFirewall(details: Record<string, any>): string | null {
  const partes: string[] = []
  if (details.action) partes.push(String(details.action))
  const protocolo = details.protocol ? String(details.protocol).toUpperCase() : null
  if (protocolo && details.port) partes.push(`${protocolo} ${details.port}`)
  else if (protocolo) partes.push(protocolo)
  else if (details.port) partes.push(`porta ${details.port}`)
  if (details.source) partes.push(`origem ${details.source}`)
  if (details.name) partes.push(String(details.name))
  return partes.length > 0 ? partes.join(' · ') : null
}

function detalheDaNic(details: Record<string, any>): string | null {
  const partes: string[] = []
  if (details.net_id) partes.push(String(details.net_id))
  // Só a VPC: a `bridge` não vem mais do backend (numa VM exposta na internet ela é a
  // bridge do próprio provedor, e este portal é white-label).
  if (details.vpc_name) partes.push(`VPC ${details.vpc_name}`)
  if (details.was_restarted) partes.push('a VM foi reiniciada para aplicar')
  return partes.length > 0 ? partes.join(' · ') : null
}

function detalheDoResetConcluido(details: Record<string, any>): string | null {
  // "Senha redefinida" sozinha não diz a única coisa que o cliente percebeu: a VM caiu,
  // ou não caiu? (#806). O rótulo é o mesmo nos dois desfechos — quem os separa é esta
  // linha, e o backend manda o `vm_reiniciada` justamente para ela.
  if (details.ja_estava_aplicada) return 'A senha pedida já era a que estava configurada'
  // A ordem espelha a do backend (`desfecho_do_reset_de_senha`: guest primeiro, reboot
  // depois). Hoje os dois nunca vêm `true` juntos — o caminho do guest sai antes de o
  // reboot rodar —, mas duas réguas em ordens opostas sobre o mesmo `details` divergem no
  // dia em que essa saída mudar, e aí a aba e a notificação contariam histórias
  // diferentes do mesmo evento.
  if (details.applied_in_guest) return 'Aplicada pelo agente convidado, sem reiniciar a VM'
  if (details.vm_reiniciada) return 'A VM foi reiniciada para aplicar a nova senha'
  // Sem nenhuma das chaves não se sabe COMO a senha entrou, e inventar "sem reiniciar"
  // seria afirmar o que ninguém mediu. Registro antigo não cai aqui — antes da #806 esta
  // `action` só era gravada no caminho do guest agent, com `applied_in_guest: true` —,
  // mas o rótulo sozinho ("Senha redefinida") continua verdadeiro sem o detalhe.
  return null
}

/**
 * Em qual VPC a VM voltou do rebuild (#791). O backend manda `vpc_atencao` (conjunto
 * fechado) quando a rede merece atenção: `palpite` — a resposta saiu de uma escolha entre
 * mais de uma VPC, sem fonte — ou `rede_mudou` — a `net0` estava fora de qualquer VPC do
 * cliente e a VM MUDOU de rede ao ser recriada. Nos dois casos a linha avisa: "silencioso"
 * era um dos impactos da issue. É `vpc_atencao`, e não o texto `vpc_aviso`, porque a
 * fronteira do histórico (`CHAVES_PUBLICAS_DO_DETALHE`) redige o texto — ele cita a bridge,
 * nome de infra do provedor — e libera só o marcador.
 */
function detalheDaVpcDoRebuild(details: Record<string, any>): string {
  const base = `VPC: ${details.vpc_name}`
  if (details.vpc_atencao === 'palpite') {
    return `${base} (escolhida automaticamente — confira a rede da VM)`
  }
  if (details.vpc_atencao === 'rede_mudou') {
    return `${base} (a VM mudou de rede ao ser reinstalada — confira a rede da VM)`
  }
  return base
}

/**
 * Título e detalhe de uma linha do histórico.
 *
 * O detalhe nunca inventa: ou sai do `details`, ou é o `message` do backend, ou é `null`.
 */
export function descreveEvento(entry: VMHistoryEntry): EventoDescrito {
  const details = entry.details || {}
  const nivel: NivelDoEvento =
    entry.level === 'error' || entry.level === 'critical'
      ? 'erro'
      : entry.level === 'warning'
        ? 'alerta'
        : 'info'

  const titulo = ROTULOS[entry.action]
  if (!titulo) {
    // Sem rótulo conhecido, o `message` cru é o que existe de verdade — mostrá-lo é menos
    // ruim do que esconder a linha ou inventar um texto.
    return { titulo: 'Evento registrado', detalhe: entry.message || entry.action, nivel }
  }

  let detalhe: string | null = null

  if (entry.action === 'vm_config_updated') {
    detalhe = detalheDaConfig(details)
  } else if (entry.action.startsWith('vm_firewall_rule_')) {
    detalhe = detalheDoFirewall(details)
  } else if (entry.action.startsWith('vm_security_group_') && details.security_group_name) {
    detalhe = `Grupo: ${details.security_group_name}`
  } else if (entry.action.startsWith('vm_nic_')) {
    detalhe = detalheDaNic(details)
  } else if (details.snapname) {
    detalhe = `Snapshot: ${details.snapname}`
  } else if (entry.action === 'vm_rebuild_requested' && details.template_name) {
    detalhe = `Template: ${details.template_name}`
  } else if (entry.action === 'vm_rebuild_completed' && details.vpc_name) {
    detalhe = detalheDaVpcDoRebuild(details)
  } else if (entry.action === 'vm_credentials_reset_requested' && details.username) {
    detalhe = `Usuário: ${details.username}`
  } else if (entry.action === 'reset_credentials_completed') {
    detalhe = detalheDoResetConcluido(details)
  } else if (entry.action.startsWith('vm_static_ip_config') && details.ip_address) {
    detalhe = `IP: ${details.ip_address}`
  } else if (entry.action.startsWith('vm_network_') && (details.vpc_name || details.vpc)) {
    // `vpc_name` nos eventos desta issue, `vpc` (só o nome) nos da #761.
    detalhe = `VPC: ${details.vpc_name || details.vpc}`
  } else if (details.public_ip) {
    detalhe = `IP público: ${details.public_ip}`
  } else if (entry.action === 'vm_creation_completed' && details.proxmox_vmid) {
    // Como a VM ganhou rede (issue #1061): em DHCP da WAN não há endereço a registrar — ele
    // vem do lease, depois do boot, e não é dado da plataforma.
    detalhe =
      details.wan_mode === 'dhcp'
        ? `VMID ${details.proxmox_vmid} · internet direto, endereço por DHCP da rede do provedor`
        : `VMID ${details.proxmox_vmid}`
  }

  // Erro sem detalhe estruturado: cai no `message`, que nos eventos de task é estável e em
  // português (a causa crua do Proxmox fica em `details.erro_tecnico`, fora da tela do
  // cliente — ela cita node, storage e caminho do provedor, e este portal é white-label).
  if (!detalhe && nivel === 'erro' && entry.message) {
    detalhe = entry.message
  }

  return { titulo, detalhe, nivel }
}

/**
 * Quem fez. Evento de task nasce sem autor — aí é a plataforma, não um usuário anônimo.
 */
export function autorDoEvento(entry: VMHistoryEntry): string {
  return entry.user_name || entry.user_email || 'sistema'
}
